import React, { useState } from "react";
import { ArrowLeftRight, Undo2, Tag } from "lucide-react";
import { fmtEuro } from "../../editor/utils";

export const TOLLERANZA_SOSTITUZIONE = 0.10;
const isDemo = (n) => /demoliz|smaltim|rimoz/i.test(n || "");
const groupOf = (v) => (isDemo(v.name) ? "DEMOLIZIONI" : v.category || "");

// Voci analoghe: stessa categoria, stessa unità, prezzo d'acquisto ±10% rispetto alla voce originale del pacchetto
export function findAlternatives(it, vociMap, items) {
  const origId = it.sostituzione?.voce_id || it.voce_id || it.id;
  const orig = vociMap[origId];
  if (!orig || !(orig.prezzo_acquisto > 0)) return [];
  const used = new Set((items || []).filter((x) => !x.excluded && x.id !== it.id).map((x) => x.voce_id || x.id));
  const g = groupOf(orig);
  return Object.values(vociMap)
    .filter((v) => v.id !== origId && v.id !== it.voce_id && !used.has(v.id) && v.unit === orig.unit && groupOf(v) === g
      && Math.abs((v.prezzo_acquisto || 0) - orig.prezzo_acquisto) <= orig.prezzo_acquisto * TOLLERANZA_SOSTITUZIONE)
    .sort((a, b) => Math.abs(a.prezzo_acquisto - orig.prezzo_acquisto) - Math.abs(b.prezzo_acquisto - orig.prezzo_acquisto));
}

export const swapItem = (x, alt) => ({
  ...x, voce_id: alt.id, name: alt.name, category: alt.category, unit_price: alt.prezzo_rivendita, unit_price_pkg: null, excluded: false,
  sostituzione: x.sostituzione || { voce_id: x.voce_id || x.id, name: x.name, category: x.category, unit_price: x.unit_price, unit_price_pkg: x.unit_price_pkg },
});

export const restoreItem = (x) => {
  const s = x.sostituzione; if (!s) return x;
  const { sostituzione, ...rest } = x;
  return { ...rest, voce_id: s.voce_id, name: s.name, category: s.category, unit_price: s.unit_price, unit_price_pkg: s.unit_price_pkg };
};

// Sconto disponibile = costo d'acquisto delle quantità incluse nelle voci scorporate (margine € invariato)
export function scorporoInfo(items, vociMap) {
  const righe = (items || []).filter((it) => it.excluded && (it.included_qty || 0) > 0).map((it) => {
    const v = vociMap[it.voce_id || it.id];
    return { id: it.id, name: it.name, importo: (it.included_qty || 0) * (v?.prezzo_acquisto || 0) };
  });
  return { righe, totale: righe.reduce((s, r) => s + r.importo, 0) };
}

function AltList({ alts, orig, isAdmin, onPick, testPrefix }) {
  return (
    <div className="space-y-1">
      {alts.map((a) => {
        const diff = orig?.prezzo_acquisto ? ((a.prezzo_acquisto - orig.prezzo_acquisto) / orig.prezzo_acquisto) * 100 : 0;
        return (
          <button key={a.id} type="button" onClick={() => onPick(a)} data-testid={`${testPrefix}-${a.id}`}
            className="w-full text-left px-2 py-1.5 border border-zinc-200 bg-white hover:border-zinc-900 hover:bg-zinc-50 rounded-sm text-xs flex items-center justify-between gap-2">
            <span className="truncate">{a.name}</span>
            {isAdmin && <span className={`mono text-[10px] shrink-0 ${diff > 0 ? "text-rose-600" : "text-emerald-700"}`}>costo {diff >= 0 ? "+" : ""}{diff.toFixed(1)}%</span>}
          </button>
        );
      })}
    </div>
  );
}

export function SwapControl({ it, items, vociMap, isAdmin, onSwap, onRestore }) {
  const [open, setOpen] = useState(false);
  const alts = findAlternatives(it, vociMap, items);
  const orig = vociMap[it.sostituzione?.voce_id || it.voce_id];
  return (
    <div className="mt-0.5">
      <div className="flex items-center gap-2 flex-wrap">
        {it.sostituzione && (
          <span className="text-[10px] bg-sky-50 text-sky-800 border border-sky-200 px-1.5 py-0.5 rounded-sm inline-flex items-center gap-1" data-testid={`lav-swapped-${it.id}`}>
            al posto di: {it.sostituzione.name}
            <button type="button" onClick={onRestore} className="underline inline-flex items-center gap-0.5" data-testid={`lav-swap-restore-${it.id}`}><Undo2 className="h-3 w-3" />ripristina</button>
          </span>
        )}
        {alts.length > 0 && (
          <button type="button" onClick={() => setOpen((o) => !o)} className="text-[10px] text-sky-700 hover:text-sky-900 inline-flex items-center gap-1 underline" data-testid={`lav-swap-btn-${it.id}`}>
            <ArrowLeftRight className="h-3 w-3" />sostituisci ({alts.length})
          </button>
        )}
      </div>
      {open && (
        <div className="mt-1.5 p-2 bg-sky-50/60 border border-sky-200 rounded-sm max-w-md" data-testid={`lav-swap-menu-${it.id}`}>
          <div className="text-[10px] text-sky-900 mb-1">Voci analoghe: il prezzo del pacchetto non cambia.</div>
          <AltList alts={alts} orig={orig} isAdmin={isAdmin} testPrefix={`lav-swap-pick-${it.id}`} onPick={(a) => { onSwap(a); setOpen(false); }} />
        </div>
      )}
    </div>
  );
}

export function ExcludedSuggestions({ it, items, vociMap, isAdmin, onSwap }) {
  const alts = findAlternatives(it, vociMap, items).slice(0, 3);
  const v = vociMap[it.voce_id || it.id];
  const importo = (it.included_qty || 0) * (v?.prezzo_acquisto || 0);
  return (
    <div className="pl-2 border-l-2 border-amber-300 mt-1 mb-1.5">
      {importo > 0 && <div className="text-[10px] text-emerald-800 inline-flex items-center gap-1" data-testid={`scorporo-importo-${it.id}`}><Tag className="h-3 w-3" />libera fino a {fmtEuro(importo)} di sconto</div>}
      {alts.length > 0 && (
        <div className="mt-1">
          <div className="text-[10px] text-zinc-600 mb-0.5">oppure sostituiscila (stessa marginalità):</div>
          <AltList alts={alts} orig={v} isAdmin={isAdmin} testPrefix={`scorporo-swap-${it.id}`} onPick={onSwap} />
        </div>
      )}
    </div>
  );
}

export function ScorporoBanner({ info, subtotal, onApply, testId }) {
  if (!info.totale) return null;
  const pct = subtotal ? (info.totale / subtotal) * 100 : 0;
  return (
    <div className="bg-emerald-50 border border-emerald-300 p-3 my-3 flex items-center justify-between gap-3 flex-wrap" data-testid={testId}>
      <div className="text-xs text-emerald-900">
        <div className="font-semibold">Sconto disponibile dalle voci scorporate: <span className="mono" data-testid={`${testId}-importo`}>{fmtEuro(info.totale)}</span> ({pct.toFixed(1)}% del subtotale)</div>
        <div className="text-[11px] text-emerald-800">{info.righe.map((r) => r.name).join(", ")} · NON applicato in automatico: lo decidi tu (valgono le regole sconto abituali).</div>
      </div>
      {onApply && <button type="button" onClick={() => onApply(Math.floor(pct * 10) / 10)} className="text-xs px-3 py-1.5 bg-emerald-700 text-white rounded-sm hover:bg-emerald-800" data-testid={`${testId}-apply`}>Usa come sconto</button>}
    </div>
  );
}
